import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../Context/AuthContext';
import { useCart } from '../../Context/CartContext';
import { Share2, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react';
import productService from '../../services/productService';
import quotationService from '../../services/quotationService';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';

// Helper function to convert color names to hex codes
const getColorHex = (colorName) => {
  const colorMap = {
    'black': '#000000',
    'charcoal': '#36454F',
    'white': '#FFFFFF',
    'red': '#DC2626',
    'blue': '#1E3A8A',
    'royal': '#4169E1',
    'yellow': '#FCD34D',
    'green': '#10B981',
    'military green': '#4B5320',
    'gray': '#6B7280',
    'grey': '#6B7280',
    'purple': '#8B5CF6',
    'brown': '#7F1D1D',
    'orange': '#F59E0B',
    'navy': '#000080',
    'sand': '#C2B280',
    'maroon': '#800000',
    'lightgray': '#D4D4D4',
    'lightgrey': '#D4D4D4',
  };
  return colorMap[colorName?.toLowerCase()] || '#000000';
};

const ProductDetails = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart, openCart } = useCart();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('description');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await productService.getProductById(productId);
        if (response.success) {
          setProduct(response.product);
          setRelatedProducts(response.relatedProducts || []);
          // Set default selections
          if (response.product.color) {
            const colors = typeof response.product.color === 'string'
              ? response.product.color.split(',').map(c => c.trim())
              : response.product.color;
            setSelectedColor(colors[0] || '');
          }
          if (response.product.sizes && response.product.sizes.length > 0) {
            setSelectedSize(response.product.sizes[0]);
          }
        } else {
          setError('Product not found');
        }
      } catch (err) {
        setError('Error loading product');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  const handleAddToCart = () => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!selectedColor) {
      alert('Please select a color');
      return;
    }

    if (!selectedSize) {
      alert('Please select a size');
      return;
    }

    // Add to cart
    addToCart(product, selectedColor, selectedSize, quantity);

    // Open cart drawer
    openCart();
  };

  const handleBuyNow = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      setSubmitting(true);
      const response = await quotationService.submitQuotation(productId, {
        color: selectedColor,
        size: selectedSize,
        quantity: quantity.toString()
      });
      if (response.success) {
        navigate('/thankyou');
      } else {
        alert('Failed to submit quotation');
      }
    } catch (err) {
      alert('Error submitting quotation');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: product.productTitle,
          url: window.location.href
        });
      } catch (err) {
        // User canceled the share dialog - silently ignore
        if (err.name === 'AbortError' || err.message === 'Share canceled') {
          return;
        }
        // For other errors, fall back to clipboard
        try {
          await navigator.clipboard.writeText(window.location.href);
          alert('Link copied to clipboard!');
        } catch (clipboardErr) {
          console.error('Failed to copy to clipboard:', clipboardErr);
        }
      }
    } else {
      try {
        await navigator.clipboard.writeText(window.location.href);
        alert('Link copied to clipboard!');
      } catch (err) {
        console.error('Failed to copy to clipboard:', err);
      }
    }
  };

  const incrementQuantity = () => setQuantity(prev => prev + 1);
  const decrementQuantity = () => setQuantity(prev => Math.max(1, prev - 1));

  // Parse colors from product
  const colors = product?.color
    ? (typeof product.color === 'string'
      ? product.color.split(',').map(c => c.trim())
      : product.color)
    : [];

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Navbar />

        <main className="flex-grow">
          {/* Breadcrumb Skeleton */}
          <div className="bg-white border-b">
            <div className="max-w-7xl mx-auto px-8 py-4">
              <div className="flex items-center">
                <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
              </div>
            </div>
          </div>

          {/* Product Details Skeleton */}
          <div className="max-w-7xl mx-auto px-8 py-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 bg-white">
              {/* Left Thumbnail Sidebar Skeleton */}
              <div className="lg:col-span-1 order-2 lg:order-1">
                <div className="flex lg:flex-col gap-4">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="w-20 h-20 bg-gray-200 rounded animate-pulse"></div>
                  ))}
                </div>
              </div>

              {/* Main Image Skeleton */}
              <div className="lg:col-span-5 order-1 lg:order-2">
                <div className="bg-gray-200 rounded-lg w-full h-[600px] animate-pulse"></div>
              </div>

              {/* Product Info Panel Skeleton */}
              <div className="lg:col-span-6 order-3 p-8">
                {/* Title */}
                <div className="h-8 bg-gray-200 rounded w-3/4 mb-4 animate-pulse"></div>

                {/* Price */}
                <div className="h-7 bg-gray-200 rounded w-32 mb-6 animate-pulse"></div>

                {/* Color Section */}
                <div className="mb-6">
                  <div className="h-4 bg-gray-200 rounded w-24 mb-3 animate-pulse"></div>
                  <div className="flex gap-3">
                    {[...Array(8)].map((_, i) => (
                      <div key={i} className="w-10 h-10 bg-gray-200 rounded-full animate-pulse"></div>
                    ))}
                  </div>
                </div>

                {/* Size Section */}
                <div className="mb-6">
                  <div className="h-4 bg-gray-200 rounded w-20 mb-3 animate-pulse"></div>
                  <div className="flex gap-2">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="w-14 h-10 bg-gray-200 rounded animate-pulse"></div>
                    ))}
                  </div>
                </div>

                {/* Quantity Selector */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-gray-200 rounded animate-pulse"></div>
                  <div className="w-20 h-10 bg-gray-200 rounded animate-pulse"></div>
                  <div className="w-10 h-10 bg-gray-200 rounded animate-pulse"></div>
                </div>

                {/* Buttons */}
                <div className="space-y-3 mb-6">
                  <div className="w-full h-12 bg-gray-200 rounded animate-pulse"></div>
                  <div className="w-full h-12 bg-gray-200 rounded animate-pulse"></div>
                </div>

                {/* Ask & Share */}
                <div className="flex gap-6 mb-8 pb-8 border-b">
                  <div className="h-5 bg-gray-200 rounded w-32 animate-pulse"></div>
                  <div className="h-5 bg-gray-200 rounded w-24 animate-pulse"></div>
                </div>

                {/* Return Policy */}
                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <div className="h-4 bg-gray-200 rounded w-full mb-2 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                </div>

                {/* Payment Methods */}
                <div className="flex items-center gap-3 pt-6 border-t">
                  <div className="h-5 bg-gray-200 rounded w-48 animate-pulse"></div>
                  <div className="flex gap-2 ml-auto">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="w-10 h-6 bg-gray-200 rounded animate-pulse"></div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs Section Skeleton */}
          <div className="bg-white border-t">
            <div className="max-w-7xl mx-auto px-8 py-12">
              <div className="border-b mb-8">
                <div className="flex gap-8">
                  <div className="h-6 bg-gray-200 rounded w-24 animate-pulse"></div>
                  <div className="h-6 bg-gray-200 rounded w-40 animate-pulse"></div>
                  <div className="h-6 bg-gray-200 rounded w-32 animate-pulse"></div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
              </div>
            </div>
          </div>

          {/* Related Products Skeleton */}
          <div className="bg-gray-50 py-16">
            <div className="max-w-7xl mx-auto px-8">
              <div className="h-8 bg-gray-200 rounded w-64 mb-8 animate-pulse"></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-white rounded-lg overflow-hidden">
                    <div className="w-full h-80 bg-gray-200 animate-pulse"></div>
                    <div className="p-4">
                      <div className="h-4 bg-gray-200 rounded w-full mb-2 animate-pulse"></div>
                      <div className="h-4 bg-gray-200 rounded w-2/3 mb-3 animate-pulse"></div>
                      <div className="flex gap-1">
                        {[...Array(5)].map((_, j) => (
                          <div key={j} className="w-5 h-5 bg-gray-200 rounded-full animate-pulse"></div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Navbar />
        <div className="flex justify-center items-center min-h-[60vh] flex-grow">
          <div className="text-red-600 text-xl">{error || 'Product not found'}</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />

      <main className="flex-grow">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-8 py-4">
            <div className="flex items-center text-sm text-gray-600">
              <Link to="/" className="hover:text-[#6B9FA8] transition-colors">Home</Link>
              <span className="mx-2">›</span>
              <span className="text-gray-900">{product.productTitle}</span>
            </div>
          </div>
        </div>

        {/* Product Details Section */}
        <div className="max-w-7xl mx-auto px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 bg-white">
            {/* Left Thumbnail Sidebar */}
            <div className="lg:col-span-1 order-2 lg:order-1">
              <div className="flex lg:flex-col gap-4 overflow-x-auto lg:overflow-visible">
                {product.images && product.images.length > 0 ? (
                  product.images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`flex-shrink-0 w-20 h-20 border-2 rounded overflow-hidden transition-all ${selectedImage === index ? 'border-black' : 'border-gray-200 hover:border-gray-400'
                        }`}
                    >
                      <img
                        src={image.path}
                        alt={`${product.productTitle} thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))
                ) : (
                  <div className="w-20 h-20 bg-gray-200 rounded"></div>
                )}
              </div>
            </div>

            {/* Main Image */}
            <div className="lg:col-span-5 order-1 lg:order-2">
              <div className="relative bg-gray-100 rounded-lg overflow-hidden">
                {product.images && product.images.length > 0 ? (
                  <img
                    src={product.images[selectedImage]?.path || product.images[0].path}
                    alt={product.productTitle}
                    className="w-full h-auto object-cover"
                  />
                ) : (
                  <div className="w-full h-96 bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-400">No image available</span>
                  </div>
                )}

                {/* Image Navigation Arrows */}
                {product.images && product.images.length > 1 && (
                  <>
                    <button
                      onClick={() => setSelectedImage(prev => Math.max(0, prev - 1))}
                      disabled={selectedImage === 0}
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setSelectedImage(prev => Math.min(product.images.length - 1, prev + 1))}
                      disabled={selectedImage === product.images.length - 1}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Product Info Panel */}
            <div className="lg:col-span-6 order-3 p-8">
              <h1 className="text-3xl font-normal mb-4">{product.productTitle}</h1>

              {/* Price */}
              <div className="mb-6">
                {user ? (
                  <p className="text-2xl font-semibold">
                    ${parseFloat(product.msrp).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                ) : (
                  <p className="text-base text-gray-700 underline cursor-pointer" onClick={() => navigate('/login')}>
                    Login to View Price
                  </p>
                )}
              </div>

              {/* Color Selection */}
              {colors.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium">Color: <span className="font-bold">{selectedColor}</span></label>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {colors.map((color, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedColor(color)}
                        className={`w-10 h-10 rounded-full border-2 transition-all ${selectedColor === color
                          ? 'border-black ring-2 ring-offset-2 ring-gray-400'
                          : 'border-gray-300 hover:border-gray-500'
                          }`}
                        style={{ backgroundColor: getColorHex(color) }}
                        title={color}
                        aria-label={`Select ${color} color`}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Size Selection */}
              {product.sizes && product.sizes.length > 0 && (
                <div className="mb-6">
                  <label className="text-sm font-medium block mb-3">Size: <span className="font-bold">{selectedSize}</span></label>
                  <div className="flex flex-wrap gap-2">
                    {product.sizes.map((size, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedSize(size)}
                        className={`px-4 py-2 border-2 rounded transition-all min-w-[60px] ${selectedSize === size
                          ? 'bg-black text-white border-black'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'
                          }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Quantity Selector */}
              <div className="mb-6">
                <div className="flex items-center gap-3">
                  <button
                    onClick={decrementQuantity}
                    className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded hover:bg-gray-100 transition"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-20 h-10 text-center border border-gray-300 rounded focus:outline-none focus:border-gray-500"
                    min="1"
                  />
                  <button
                    onClick={incrementQuantity}
                    className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded hover:bg-gray-100 transition"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3 mb-6">
                <button
                  onClick={handleAddToCart}
                  className="w-full bg-black text-white py-3 px-6 rounded hover:bg-gray-800 transition"
                >
                  {user ? 'Add to cart' : 'Add to cart - Login to View Price'}
                </button>
                <button
                  onClick={handleBuyNow}
                  disabled={submitting}
                  className="w-full bg-red-600 text-white py-3 px-6 rounded hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Processing...' : 'BUY IT NOW'}
                </button>
              </div>

              {/* Ask Question & Share */}
              <div className="flex items-center gap-6 mb-8 pb-8 border-b">
                <button
                  onClick={() => navigate('/contactus')}
                  className="flex items-center gap-2 text-sm text-gray-700 hover:text-black transition"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" strokeWidth="2" />
                    <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" strokeWidth="2" strokeLinecap="round" />
                    <circle cx="12" cy="17" r="0.5" fill="currentColor" strokeWidth="1" />
                  </svg>
                  Ask a question
                </button>
                <button
                  onClick={handleShare}
                  className="flex items-center gap-2 text-sm text-gray-700 hover:text-black transition"
                >
                  <Share2 className="w-5 h-5" />
                  Share
                </button>
              </div>

              {/* Return Policy */}
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <div className="flex items-start gap-3 mb-4">
                  <RotateCcw className="w-5 h-5 text-gray-700 mt-1" />
                  <div>
                    <p className="text-sm font-medium mb-1">Return within <strong>days</strong> of purchase.</p>
                    <p className="text-sm text-gray-600">Duties & taxes are non-refundable.</p>
                  </div>
                </div>
              </div>

              {/* Payment Methods */}
              <div className="flex items-center gap-3 pt-6 border-t">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
                  </svg>
                  <span className="text-sm font-medium text-gray-700">Guaranteed Safe Checkout</span>
                </div>
                <div className="flex gap-2 ml-auto">
                  <div className="w-10 h-6 bg-blue-600 rounded flex items-center justify-center text-white text-[10px] font-bold">
                    VISA
                  </div>
                  <div className="w-10 h-6 bg-blue-500 rounded flex items-center justify-center text-white text-[10px] font-bold">
                    PP
                  </div>
                  <div className="w-10 h-6 bg-red-600 rounded flex items-center justify-center text-white text-[10px] font-bold">
                    MC
                  </div>
                  <div className="w-10 h-6 bg-blue-400 rounded flex items-center justify-center text-white text-[10px] font-bold">
                    AMEX
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Description Tabs Section */}
        <div className="bg-white border-t">
          <div className="max-w-7xl mx-auto px-8 py-12">
            {/* Tabs */}
            <div className="border-b mb-8">
              <div className="flex gap-8">
                <button
                  onClick={() => setActiveTab('description')}
                  className={`pb-4 px-2 font-medium transition-colors relative ${activeTab === 'description'
                    ? 'text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-black'
                    : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                  Description
                </button>
                <button
                  onClick={() => setActiveTab('additional')}
                  className={`pb-4 px-2 font-medium transition-colors relative ${activeTab === 'additional'
                    ? 'text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-black'
                    : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                  Additional Information
                </button>
                <button
                  onClick={() => setActiveTab('return')}
                  className={`pb-4 px-2 font-medium transition-colors relative ${activeTab === 'return'
                    ? 'text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-black'
                    : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                  Return Policies
                </button>
              </div>
            </div>

            {/* Tab Content */}
            <div className="prose max-w-none">
              {activeTab === 'description' && (
                <div>
                  <p className="text-gray-700 mb-4">{product.productDescription}</p>
                  <ul className="space-y-2 text-gray-700">
                    <li>• 3-End Fleece</li>
                    <li>• Label Type: "EASY" tear-away label</li>
                    <li>• Style Weight: 8.3 oz</li>
                    <li>• 80% Ring-Spun Cotton / 20% Polyester</li>
                    <li>• Fit: Classic Fit</li>
                    <li>• Sizes: S-3XL</li>
                    <li>• Case: 24 pcs. (S-3XL)</li>
                    <li>• Unisex</li>
                  </ul>
                </div>
              )}
              {activeTab === 'additional' && (
                <div>
                  <table className="w-full">
                    <tbody>
                      <tr className="border-b">
                        <td className="py-3 font-medium">Item Number</td>
                        <td className="py-3 text-gray-700">{product.itemNumber || 'N/A'}</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-3 font-medium">Category</td>
                        <td className="py-3 text-gray-700">{product.category || 'N/A'}</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-3 font-medium">Available Colors</td>
                        <td className="py-3 text-gray-700">{colors.join(', ')}</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-3 font-medium">Available Sizes</td>
                        <td className="py-3 text-gray-700">{product.sizes?.join(', ') || 'N/A'}</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-3 font-medium">Case Pack</td>
                        <td className="py-3 text-gray-700">{product.casePack || 'N/A'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
              {activeTab === 'return' && (
                <div>
                  <p className="text-gray-700 mb-4">
                    We accept returns within 30 days of purchase. Items must be unworn, unwashed, and in original condition with all tags attached.
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Returns must be initiated within 30 days of delivery</li>
                    <li>• Products must be in original condition</li>
                    <li>• Duties and taxes are non-refundable</li>
                    <li>• Refunds will be processed within 5-7 business days</li>
                    <li>• Customer is responsible for return shipping costs</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* People Also Bought Section */}
        {relatedProducts.length > 0 && (
          <div className="bg-gray-50 py-16">
            <div className="max-w-7xl mx-auto px-8">
              <h2 className="text-3xl font-normal mb-8">People Also Bought</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                {relatedProducts.slice(0, 4).map((relatedProduct) => {
                  const relatedColors = relatedProduct.color
                    ? (typeof relatedProduct.color === 'string'
                      ? relatedProduct.color.split(',').map(c => c.trim())
                      : relatedProduct.color)
                    : [];

                  return (
                    <Link
                      key={relatedProduct._id}
                      to={`/product/${relatedProduct._id}`}
                      className="group bg-white rounded-lg overflow-hidden hover:shadow-lg transition"
                    >
                      <div className="relative bg-gray-100">
                        <img
                          src={relatedProduct.images && relatedProduct.images[0] ? relatedProduct.images[0].path : 'https://via.placeholder.com/300'}
                          alt={relatedProduct.productTitle}
                          className="w-full h-80 object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-medium mb-2 line-clamp-2">{relatedProduct.productTitle}</h3>
                        <p className="text-sm text-gray-600 mb-3">
                          {user ? `$${parseFloat(relatedProduct.msrp).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'From Login to View Price'}
                        </p>
                        {relatedColors.length > 0 && (
                          <div className="flex gap-1 flex-wrap">
                            {relatedColors.slice(0, 8).map((color, i) => (
                              <span
                                key={i}
                                className="w-5 h-5 rounded-full border border-gray-300"
                                style={{ backgroundColor: getColorHex(color) }}
                              ></span>
                            ))}
                          </div>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default ProductDetails;
